import Modal from "./Modal";
import { useForm, useFieldArray } from "react-hook-form";
import { useState, useEffect, useRef, useMemo } from "react";
import ModalRemove from "./ModalRemove";
import { useDispatch, useSelector } from "react-redux";
import {
  obtenerPerfiles,
  eliminarPerfil,
  actualizarPerfil,
  almacenarPerfil,
} from "../../redux/perfilSlice";
import { obtenerDocumentos } from "../../redux/genericosSlice";
import { toast } from "react-toastify";
import WindowDimension from "../../Hooks/WindowDimension";
import Required from "../../Components/Required";
import { OrderTable } from "../../Components/OrderTable";
import useSubmit from "../../Hooks/useSubmit";
import Pagination from "./Paginacion";
import { getAdminProfilesPage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { Skeleton } from "antd";
import { useValidation } from "../../Hooks/useValidation";
import { _translateValue } from "../translation-gestor/translateValue";

const SCREEN_NAME = "PantallaListaDocumentos";

function ListaDocumentos(props) {
  const { validation } = useValidation();
  const { locale } = useLocale();
  const [adminProfilesPage, setAdminProfilesPage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [modalState, changeModalState] = useState(false);
  const [removeState, changeRemoveState] = useState(false);
  const [registerSelected = [], selectReg] = useState();
  const [grupos = [], setGrupos] = useState();
  const [tituloModal, setTituloModal] = useState("Agregar perfil");
  const [translatedProfiles, setTranslatedProfiles] = useState({});
  const [translatedDocuments, setTranslatedDocuments] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);

  const { width } = WindowDimension();
  const dispatch = useDispatch();
  let busqueda = useRef();

  const { perfiles } = useSelector((state) => state.perfiles);
  const { documentos } = useSelector((state) => state.genericos);
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    control,
    setValue,
  } = useForm({
    defaultValues: {
      requiereVerificacionSicobatec: false,
      perfilNombre: "",
      perfilId: 0,
      documentos: [{ documentoId: undefined, esObligatorio: false }],
    },
  });

  const { submitAction } = useSubmit();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "documentos",
  });

  const columns = useMemo(() => {
    const profileTable = adminProfilesPage?.profile_table ?? [];
    return [
      { label: profileTable[0]?.label || 'Código', accessor: 'perfilId', sortable: true },
      { 
        label: profileTable[1]?.label || 'Perfil', 
        accessor: 'perfilNombre', 
        sortable: true,
        render: (item) => (
          <td>{translatedProfiles[item.perfilNombre] || item.perfilNombre}</td>
        ),
      },
      {
        label: profileTable[2]?.label || 'Documentos requeridos',
        accessor: 'nombreCompleto',
        sortable: false,
        render: (item) => (
          <td>
            <ul>
              {item.documentos.map((p, i) => (
                <li key={i}>{translatedDocuments[p.documentoNombre] || p.documentoNombre}</li>
              ))}
            </ul>
          </td>
        ),
      },
      { label: profileTable[3]?.label || 'Acción', accessor: 'action', sortable: false },
    ];
  }, [adminProfilesPage, translatedProfiles, translatedDocuments]);

  const showModalEdit = (reg) => {
    reset(reg);
    setTituloModal(adminProfilesPage?.subtitleO || "Editar perfil");

    changeModalState(true);
    selectReg(reg);
  };

  const closeModalEdit = () => {
    reset({ perfilId: 0, perfilNombre: "", documentos: [] });
    changeModalState(false);
  };

  const showRemoveModalEdit = (reg) => {
    selectReg(reg);
    changeRemoveState(true);
  };

  const closeRemoveModalEdit = () => {
    selectReg();
    changeRemoveState(false);
  };

  const cargarPerfiles = () => {
    dispatch(obtenerPerfiles());
  };

  const cargarPerfilesPorCaracter = () => {
    dispatch(obtenerPerfiles(busqueda.current.value));
  };

  const eliminar = async () => {
    submitAction(eliminarPerfil, registerSelected.perfilId, () => {
      closeRemoveModalEdit();
      cargarPerfiles();
    });
  };

  const actualizar = async (data) => {
    data.documentos.forEach((el) => {
      if (!el.grupo) {
        el.grupo = undefined;
      }
    });

    if (data.documentos.length === 0) {
      toast.error(validation?.document_not_selected || "Debe seleccionar algún documento");
      return false;
    }

    submitAction(
      data.perfilId > 0 ? actualizarPerfil : almacenarPerfil,
      data,
      () => {
        closeModalEdit();
        cargarPerfiles();
      }
    );
  };

  useEffect(() => {
    cargarPerfiles();
    dispatch(obtenerDocumentos());

    let array = [];
    for (let index = 1; index <= 10; index++) {
      array.push(index);
    }
    setGrupos(array);
  }, [dispatch]);

  useEffect(() => {
    if (perfiles) {
      setValue(
        "requiereVerificacionSicobatec",
        perfiles.requiereVerificacionSicobatec
      );
      setValue("documentos", perfiles.documentos);
      setValue("perfilId", perfiles.perfilId);
      setValue("perfilNombre", perfiles.perfilNombre);
    }
  }, [perfiles]);

  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getAdminProfilesPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setAdminProfilesPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => { 
        if (!cancel) {
          setAdminProfilesPage(null);
          setIsLoadingContent(false);
        }
      });
    return () => { cancel = true };
  }, [locale])

  // Efecto para traducir los nombres de perfiles y documentos cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasProfiles = perfiles && Array.isArray(perfiles) && perfiles.length > 0;
      const hasDocuments = documentos && Array.isArray(documentos) && documentos.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasProfiles && !hasDocuments) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const profileTranslations = {};
      const documentTranslations = {};

      // Traducir perfiles
      if (hasProfiles) {
        const profilePromises = perfiles.map(async (profile) => {
          if (profile.perfilNombre) {
            const translated = await _translateValue(SCREEN_NAME, profile.perfilNombre, locale);
            profileTranslations[profile.perfilNombre] = translated;
          }
        });
        await Promise.all(profilePromises);
      }

      // Traducir documentos
      if (hasDocuments) {
        const documentPromises = documentos.map(async (document) => {
          if (document.documentoNombre) {
            const translated = await _translateValue(SCREEN_NAME, document.documentoNombre, locale);
            documentTranslations[document.documentoNombre] = translated;
          }
        });
        await Promise.all(documentPromises);
      }

      // Actualizar estados y finalizar traducción
      setTranslatedProfiles(profileTranslations);
      setTranslatedDocuments(documentTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [perfiles, documentos, locale]);

  if (isLoadingContent || isTranslating) {
    return (
      <div className="main-admin">
        <div className="main-admin-bar">
          <Skeleton.Button style={{ width: 40, height: 40 }} active />
          <Skeleton.Input style={{ width: 300, height: 30 }} active />
          <div className="header-admin-bar-items ml-auto">
            <Skeleton.Input style={{ width: 200, height: 40 }} active />
          </div>
        </div>
        <div className="table-container">
          <Skeleton.Input style={{ width: '100%', height: 400, minWidth: '800px' }} active />
        </div>
      </div>
    );
  }

  return (
    <div className="main-admin">
      <div className="main-admin-bar">
        <button
          className="main-admin-button"
          onClick={() => {
            changeModalState(true);
            setTituloModal(adminProfilesPage?.subtitle || "Nuevo Perfil");
            reset();
          }}
        >
          +
        </button>
        <h2>{adminProfilesPage?.title || "Definición de perfiles"}</h2>
        <div className="header-admin-bar-items ml-auto">
          <input
            type="search"
            className="form-control"
            placeholder={adminProfilesPage?.search_input || "Búsqueda"}
            onInput={cargarPerfilesPorCaracter}
            ref={busqueda}
          />
        </div>
      </div>

      <div className="table-container">
        {perfiles.length > 0 ? (
          <>
            <OrderTable
              {...{ columns }}
              data={perfiles}
              clase="table-adm"
              actions={[
                {
                  icon: "",
                  clase: "text-primary",
                  event: (item) => showModalEdit(item),
                },
                {
                  icon: "",
                  clase: "text-primary",
                  event: (item) => showRemoveModalEdit(item),
                },
              ]}
            />
          </>
        ) : (
          <div className="no-results-container">
            <img src={props.img} alt="Imagen sin resultados" />
            <h1>{props.noResultMessage}</h1>
          </div>
        )}
      </div>

      <Modal
        estado={modalState}
        cambiarEstado={closeModalEdit}
        titulo={tituloModal}
        clase={`${width < 576 ? "full-width" : "medium"}`}
        buttonGroup={
          <>
            <button
              type="submit"
              className="button-text"
              onClick={handleSubmit(actualizar)}
            >
              {adminProfilesPage?.save_button || "Guardar"}
            </button>
          </>
        }
      >
        <form>
          <div className="grid-container col-2">
            <div className="form-group mr-2">
              <label>{adminProfilesPage?.profile_name || "Nombre del perfil"}</label>
              <Required />
              <input
                className="form-control"
                type="text"
                {...register("perfilNombre", { required: validation?.required_field || "Campo requerido" })}
              ></input>
              {errors.perfilNombre && (
                <p className="error">{errors.perfilNombre.message}</p>
              )}
            </div>
            <div className="align-self-center">
              <div className="d-flex">
                <label>{adminProfilesPage?.question_verify || "¿Requiere verificación en Sicobatec?"}</label>
                <input
                  {...register("requiereVerificacionSicobatec")}
                  type="checkbox"
                  className="form-control-check d-flex"
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>{adminProfilesPage?.documents || "Documentos:"}</label>
            <Required />

            <ul>
              {fields.map((item, index) => {
                return (
                  <li key={"el_" + index}>
                    <div className="form-group flex-form gap-20">
                      <select
                        className="form-control"
                        name={`documentos.${index}.documentoId`}
                        control={control}
                        {...register(`documentos.${index}.documentoId`, {
                          required: validation?.required_field || "Campo requerido",
                        })}
                      >
                        <option value="">{adminProfilesPage?.select_optionA || "Seleccione"}</option>
                        {documentos?.map((d, i) => (
                          <option key={d.documentoId} value={d.documentoId}>
                            {translatedDocuments[d.documentoNombre] || d.documentoNombre}
                          </option>
                        ))}
                      </select>
                      <div className="d-flex">
                        <label>{adminProfilesPage?.question_required || "¿Es obligatorio?"}</label>
                        <input
                          name={`documentos.${index}.esObligatorio`}
                          type="checkbox"
                          className="form-control-check d-flex"
                          {...register(`documentos.${index}.esObligatorio`, {
                            required: false,
                          })}
                        />
                      </div>

                      <select
                        className="form-control"
                        control={control}
                        {...register(`documentos.${index}.grupo`)}
                      >
                        <option value="">{adminProfilesPage?.select_optionB || "Sin grupo"}</option>
                        {grupos?.map((d, i) => (
                          <option key={d} value={d}>
                            Grupo {d}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="modBtn"
                      >
                        <i className="material-icons"></i>
                      </button>
                    </div>
                    {errors.documentos?.[index]?.documentoId && (
                      <p className="error">
                        {errors.documentos?.[index]?.documentoId.message}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
            <div>
              <button
                type="button"
                className="button-text"
                onClick={() => {
                  append({
                    documentoId: "",
                    esObligatorio: false,
                  });
                }}
              >
                {adminProfilesPage?.add_button || "Agregar documento"}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      <ModalRemove
        estado={removeState}
        closeModalHandled={changeRemoveState}
        title={adminProfilesPage?.subtitleT || "Eliminar perfil"}
        message={adminProfilesPage?.question || "¿Seguro que desea eliminar el siguiente perfil?"}
        item={registerSelected.perfil}
        icon={null}
        btnGroup={
          <>
            <button className="button-text text-red" onClick={eliminar}>
              {adminProfilesPage?.confirm_button || "Confirmar"}
            </button>
            <button
              className="button-text text-grey"
              onClick={() => {
                closeRemoveModalEdit(false);
              }}
            >
              {adminProfilesPage?.cancel_button || "Cancelar"}
            </button>
          </>
        }
      />
    </div>
  );
}

export default ListaDocumentos;
